import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, useCallback, useMemo, useRef, useState, type ReactNode} from 'react'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestBasePath} from '../../stores/selectors'
import {
  useCommentsPanelViewActions,
  useCommentsPanelViewMode,
  useLayoutAuthorLogin,
  useLayoutCanComment,
} from '../../stores/layout-selectors'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {createPortal} from 'react-dom'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useDockedPanelMetadata,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './docked-side-panel-constants'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import {Comments} from '../comments/Comments'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {isIssueCommentComposerEnabled} from '../comments/IssueCommentComposer'
import {CommentsContext} from '../comments/CommentsContext'
import {CommentsSidePanelFooter, type CommentsSidePanelFooterHandle} from './CommentsSidePanelFooter'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'

export interface CommentsSidePanelProps {
  pageLimits?: PageLimits
}

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments.
 *
 * Self-contained: sources its own data from hooks/context.
 *
 * Rendering target depends on the `updated_pull_request_header` flag:
 * - **ON**: rendered by `DockedSidePanelContent` and emits `<Panel>` inline; that
 *   parent already lives inside the docked sidebar.
 * - **OFF**: rendered as a child of `OpenCommentsPanelButton` (to inherit
 *   `PullRequestStoreProvider` scope needed by `ThreadPositioningWithStore`),
 *   and portals `<Panel>` into `DOCKED_SIDE_PANEL_CONTENT_ID` so it visually
 *   appears in the sidebar.
 *
 * In compact mode, falls back to a Dialog sidesheet regardless of flag.
 */
export const CommentsSidePanel = memo(function CommentsSidePanelComponent({pageLimits}: CommentsSidePanelProps) {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()
  const canComment = useLayoutCanComment()

  // The panel's view state lives in the layout store so it survives this component unmounting
  // when the panel closes. It is mapped onto `CommentsContext` here so the comments subtree
  // stays reusable by consumers that supply their own state instead.
  const viewMode = useCommentsPanelViewMode()
  const {setCommentsPanelViewMode} = useCommentsPanelViewActions()

  const commentsContextValue = useMemo(
    () => ({
      viewMode,
      setViewMode: setCommentsPanelViewMode,
      commentAnchorPrefix: 'panel-r',
    }),
    [viewMode, setCommentsPanelViewMode],
  )

  // Apply panel metadata (e.g. author filter from action items)
  const panelMetadata = useDockedPanelMetadata() ?? undefined

  const {filterState, setCurrentFilterState} = useCommentsFilterState(viewMode)

  const {filteredConversationItems, selectedReviewId, totalConversationItemsCount, authorList, isReviewCommentsMode} =
    useFilteredCommentsData({
      filterState,
      filterOverrides: panelMetadata,
    })

  // Apply view mode from each new panel metadata payload, if specified
  const [lastAppliedMetadata, setLastAppliedMetadata] = useState<typeof panelMetadata>()
  if (panelMetadata !== lastAppliedMetadata) {
    setLastAppliedMetadata(panelMetadata)
    if (isOpen && panelMetadata?.viewMode) {
      setCommentsPanelViewMode(panelMetadata.viewMode)
    }
  }

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<CommentsSidePanelFooterHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => footerRef.current?.quoteReply(quotedText), [])

  const isCommentPanelFooterEnabled = isIssueCommentComposerEnabled(isReviewCommentsMode, canComment)
  const commentsFooter = isCommentPanelFooterEnabled ? <CommentsSidePanelFooter ref={footerRef} /> : null
  // Legacy compact Dialog renders the footer without the css-anchor-positioning
  // feature flag wrapper — preserve that to avoid behavior change.
  const commentPanelFooter = commentsFooter ? <Dialog.Footer>{commentsFooter}</Dialog.Footer> : null

  const limitExceeded = pageLimits?.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits?.reviewThreadsPaginatedLimit

  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')

  if (!isOpen) return null

  const withCommentsContext = (children: ReactNode) => (
    <CommentsContext value={commentsContextValue}>{children}</CommentsContext>
  )

  if (!isCompact) {
    const panelContent = (
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          <CommentsViewControls
            authorList={authorList}
            filterState={filterState}
            setFilterState={setCurrentFilterState}
          />
        </Panel.Header>
        <Panel.Body>
          <Comments
            filteredConversationItems={filteredConversationItems}
            selectedReviewId={selectedReviewId}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            totalConversationItemsCount={totalConversationItemsCount}
            limitCount={limitCount}
            isReviewCommentsMode={isReviewCommentsMode}
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>
    )

    // When the updated_pull_request_header flag is OFF, the panel is rendered
    // as a child of OpenCommentsPanelButton (so it inherits PullRequestStoreProvider
    // scope needed by ThreadPositioningWithStore). It must portal into the
    // central docked-panel target div so it visually appears in the sidebar.
    // When the flag is ON, the panel is rendered directly inside that target div
    // by DockedSidePanelContent and no portal is needed.
    if (!updatedPullRequestHeader) {
      const portalTarget = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)
      if (portalTarget) {
        return createPortal(withCommentsContext(panelContent), portalTarget)
      }
      return null
    }

    return withCommentsContext(panelContent)
  }

  // Flag-off compact carve-out: CompactSidePanelDialog skips Comments under
  // !updated_pull_request_header, so keep the legacy per-panel Dialog here.
  const compactCommentsBody = (
    <Comments
      limitExceeded={limitExceeded}
      limitCount={limitCount}
      onNavigateToDiffComment={closePanel}
      filteredConversationItems={filteredConversationItems}
      handleQuoteReply={handleQuoteReply}
      pathName={pathName}
      selectedReviewId={selectedReviewId}
      totalConversationItemsCount={totalConversationItemsCount}
      pullRequestAuthorLogin={pullRequestAuthorLogin}
      isReviewCommentsMode={isReviewCommentsMode}
    />
  )

  if (!updatedPullRequestHeader) {
    return withCommentsContext(
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderFooter={() => {
          return commentPanelFooter
        }}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.Comments)}</Dialog.Title>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close comments panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
            <CommentsViewControls
              authorList={authorList}
              filterState={filterState}
              setFilterState={setCurrentFilterState}
            />
          </Dialog.Header>
        )}
      >
        {compactCommentsBody}
      </Dialog>,
    )
  }

  // Flag-on compact: portal full content into the shared CompactSidePanelDialog.
  return withCommentsContext(
    <>
      <CompactPanel.Header>
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
        />
      </CompactPanel.Header>
      <CompactPanel.Content>{compactCommentsBody}</CompactPanel.Content>
      {isCommentPanelFooterEnabled && (
        <CompactPanel.Footer>
          <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
        </CompactPanel.Footer>
      )}
    </>,
  )
})
